import { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { popupcontext } from "../../context/Popupscontext";
import { filtrationcontext } from "../../context/Filtercontext";
import { toast } from "react-toastify";
import trans from "../../utils";
import Actions from "../antd/Table/Actions";
import AntdTable from "../antd/Table/Table";
import { Tag } from "antd";
import { useGetAllCustomersQuery } from "../../Redux/service/Customer/Customer";
import CustomersHead from "./CustomersParts/CustomersHead";
import CustomerForm from "./CustomersForm/CustomerForm";
import { formatDateString } from "../../JsHelpers/DateFormat";
import usePermission from "../../hooks/usePermission";

export default function Customers() {
	const [currentPage, setCurrentPage] = useState(1);
	const { setEdited, deleteItem, setDeleteItem, setReloadData, reloadData } =
		useContext(popupcontext);
	const { create, edit, delete: canDelete } = usePermission("SETUPUSERROLES");

	const buildQueryString = (filterObj) => {
		const query = Object.entries(filterObj)
			.filter(
				([_, value]) => value !== null && value !== "" && value !== undefined
			)
			.map(([key, value]) => `${key}=${value}`)
			.join("&");

		return query ? `?${query}` : "";
	};

	const [filter, setFilter] = useState({
		status: null,
	});
	const queryString = buildQueryString(filter);
	const {
		limit,
		setLimit,
		setSort: SetSortFilter,
		searchQuery,
		setSearchQuery,
	} = useContext(filtrationcontext);
	const [sort, setSort] = useState({
		by: "",
		order: "",
	});

	let {
		data: AllCustomers,
		isLoading,
		isError,
		refetch,
	} = useGetAllCustomersQuery(
		{
			page: currentPage,
			limit: limit,
			search: searchQuery,
			sortby: sort.by,
			sortorder: sort.order,
			queryString: queryString,
		},
		{
			skip: !currentPage || !limit,
			refetchOnMountOrArgChange: true,
		}
	);

	useEffect(() => {
		return () => {
			SetSortFilter({ type: "", value: "" });
			setSearchQuery("");
		};
	}, [SetSortFilter, setSearchQuery]);

	useEffect(() => {
		setCurrentPage(1);
	}, [searchQuery]);

	useEffect(() => {
		localStorage.setItem("page", currentPage);
	}, [currentPage]);
	useEffect(() => {
		if (reloadData) {
			refetch();
		}
		setReloadData(false);
	}, [reloadData]);
	useEffect(() => {
		async function fetchdata() {
			if (deleteItem.condition === true && deleteItem.value === "customer") {
				await refetch();
				setDeleteItem({ condition: null, value: null });
			}
		}
		fetchdata();
	}, [deleteItem.condition, deleteItem.value, refetch, setDeleteItem]);

	if (isError)
		return toast.error(trans("Please try again"), {
			autoClose: 800,
			rtl: localStorage.getItem("lang") === "ar" ? true : false,
		});

	const checkExpire = (expirationTime) => {
		return Date.now() <= new Date(expirationTime).getTime();
	};

	const customersColumns = [
		{
			title: trans("Name"),
			dataIndex: "name",
			sorter: true,
			fixed: "left",
			key: "name",
			width: 180,
			render: (name) => <span>{name}</span>,
		},
		{
			title: trans("Email"),
			sorter: true,
			dataIndex: "email",
			key: "email",
			width: 180,
			render: (email) => <span>{email || "- - - - -"}</span>,
		},
		{
			title: trans("Phone Number"),
			sorter: true,
			dataIndex: "phoneNumber",
			key: "phoneNumber",
			width: 150,
			render: (phoneNumber) => <span>{phoneNumber || "- - - - -"}</span>,
		},
		{
			title: trans("Address"),
			dataIndex: "address",
			key: "address",
			width: 150,
			render: (address) => <span>{address || "- - - - -"}</span>,
		},
		{
			title: trans("Country"),
			sorter: true,
			dataIndex: "country",
			key: "country",
			width: 150,
			render: (country) => (
				<span className="text-capitalize">{trans(country) || "- - - - -"}</span>
			),
		},
		{
			title: trans("City"),
			sorter: true,
			dataIndex: "city",
			width: 150,
			key: "city",
			render: (city) => {
				const value = trans(city);
				const formatted =
					value && typeof value === "string"
						? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
						: "- - - - -";

				return <span className="text-capitalize">{trans(formatted)}</span>;
			},
		},
		/*{
      title: trans("Type"),
      key: "type",
      render: (_, record) => <span>{getType(record)}</span>,
    },*/
		{
			title: trans("Status"),
			key: "status",
			sorter: true,
			width: 150,
			dataIndex: "status",
			render: (status) => {
				return (
					<Tag color={status ? "green" : "red"}>
						{status ? trans("Active") : trans("Expired")}
					</Tag>
				);
			},
		},
		{
			title: trans("Created By"),
			sorter: true,
			dataIndex: "createdBy",
			key: "createdBy",
			width: 180,
			render: (createdBy) => <span>{createdBy?.name}</span>,
		},
		{
			title: trans("Created At"),
			sorter: true,
			dataIndex: "createdAt",
			key: "createdAt",
			width: 200,
			render: (createdAt) => <span>{formatDateString(createdAt)}</span>,
		},
		{
			title: trans("Actions"),
			key: "actions",
			fixed: "right",
			width: 120,
			render: (_, record) => (
				<Actions
					customerId={record.id}
					name={record.name}
					type="customer"
					expired={!checkExpire(record.expirationTime)}
					canAddTask={create}
					canEdit={edit}
					canDelete={canDelete}
				/>
			),
		},
	];

	const handleTableChange = (pagination, filters, sorter) => {
		if (sorter.order) {
			setSort({
				by: sorter.field,
				order: sorter.order === "ascend" ? "asc" : "desc",
			});
		} else {
			setSort({
				by: "",
				order: "",
			});
		}
	};

	return (
		<div className="users">
			<Helmet>
				<title>Customers</title>
				<meta name="description" content="customer" />
			</Helmet>

			<CustomersHead setFilter={setFilter} setCurrentPage={setCurrentPage} />

			<div className="bg-white-rounded-top table-parent">
				<AntdTable
					columns={customersColumns}
					dataSource={AllCustomers?.data || []}
					pageSize={limit}
					loading={isLoading}
					totalItems={AllCustomers?.totalResults || 0} // Total count for pagination
					currentPage={currentPage}
					onPageChange={(page) => setCurrentPage(page)}
					onLimitChange={(newLimit) => {
						setLimit(newLimit);
						setCurrentPage(1);
					}}
					onTableChange={handleTableChange}
				/>
			</div>

			<CustomerForm />
		</div>
	);
}
